import React, { useState } from 'react';
import { Phone, X, Clock, Check } from 'lucide-react';

const CallMeBackButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [preferredTime, setPreferredTime] = useState('ASAP');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/callback-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone.trim(),
          preferred_time: preferredTime,
          name: name.trim() || 'Not provided'
        })
      });

      if (response.ok) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsOpen(false);
          setIsSuccess(false);
          setPhone('');
          setName('');
          setPreferredTime('ASAP');
        }, 2500);
      }
    } catch (error) {
      console.error('Failed to submit callback request:', error);
      alert('Failed to submit. Please try again or call us directly at 021 327 944');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white rounded-full shadow-2xl flex items-center gap-2 px-5 py-4 transition-all hover:scale-105 animate-pulse hover:animate-none"
        style={{ boxShadow: '0 4px 20px rgba(255, 107, 0, 0.4)' }}
      >
        <Phone className="w-6 h-6" />
        <span className="font-bold text-lg">Book a Quick Call</span>
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="bg-[#1a4d2e] text-white p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-full">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Book a Quick Call</h2>
                  <p className="text-sm text-white/80">We'll call you - no waiting!</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Success State */}
            {isSuccess ? (
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#1a4d2e] mb-2">Got it!</h3>
                <p className="text-gray-600">We'll give you a call {preferredTime === 'ASAP' ? 'as soon as possible' : preferredTime.toLowerCase()}.</p>
              </div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="p-6">
                {/* Phone Number - Primary Field */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="021 XXX XXXX"
                    className="w-full px-4 py-4 text-xl border-2 border-gray-200 rounded-xl focus:border-[#ff6b00] focus:ring-2 focus:ring-[#ff6b00]/20 outline-none transition-all"
                    required
                    autoFocus
                  />
                </div>

                {/* Preferred Time */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Best time to call?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {['ASAP', 'Morning', 'Afternoon', 'Evening'].map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setPreferredTime(time)}
                        className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                          preferredTime === time
                            ? 'border-[#ff6b00] bg-[#ff6b00]/10 text-[#ff6b00]'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Optional Name */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name (optional)
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="So we know who to ask for"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1a4d2e] focus:ring-2 focus:ring-[#1a4d2e]/20 outline-none transition-all"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !phone.trim()}
                  className="w-full bg-[#ff6b00] hover:bg-[#ff6b00]/90 disabled:bg-gray-300 text-white font-bold text-lg py-4 rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Phone className="w-5 h-5" />
                      Book My Call
                    </>
                  )}
                </button>

                {/* Direct Call Option */}
                <p className="text-center text-sm text-gray-500 mt-4">
                  Or call us directly: <a href="tel:021327944" className="text-[#1a4d2e] font-semibold hover:underline">021 327 944</a>
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CallMeBackButton;
